import { Box } from "@mui/material";
import { useApi } from "@platform/api";
import { CoverageTierEnum } from "@platform/openapi-types";

import { useUpdateRate, useUpdateProposalRate } from "../../../hooks/useRates";
import { formatRateValue } from "../../../utils/rateFormatters";
import { parseIntBaseTen } from "../../../utils/transforms";
import { buildRateFieldPath } from "../../../utils/rateFieldPath";

import { DebouncedNumericInput } from "./NumericInput";
import { RateReviewCell } from "./RateReviewCell";
import {
  AG_BORDER,
  gridCellSx,
  gridRowSx,
  headerCellSx,
  inputSx,
} from "./constants";

import type { FC } from "react";
import type { Rate, RateSchedule } from "../../../types";

const getCompanionRateLabel = (coverageTierCode?: CoverageTierEnum): string => {
  switch (coverageTierCode) {
    case CoverageTierEnum.childOnly:
      return "Child";
    case CoverageTierEnum.spouseChildren:
      return "Dependents";
    default:
      return "";
  }
};

export interface CompanionRatesSectionProps {
  companionRates: Rate[];
  companionRateSchedule: RateSchedule;
  planId: string;
  /** Total number of CSS grid columns (including the label column and any renewal columns) */
  totalColumns: number;
  /** Trailing renewal columns to skip when spanning volume/rate, then fill with blanks. */
  renewalColumns?: number;
  /** Explicit column span for the volume header; overrides Math.floor((currentColumns-1)/2) */
  volumeSpan?: number;
  /** Explicit column span for the rate header; overrides the derived value */
  rateSpan?: number;
  onRateSaved: () => void;
  isProposalRateSchedule?: boolean;
}

/**
 * Reusable companion (dependent) rates section for age-banded renderers.
 *
 * Renders a header row + data rows for companion flat rate schedules
 * (e.g., Child, Dependents) at the bottom of a CSS Grid container.
 * Uses display: contents so children integrate as direct grid items.
 */
export const CompanionRatesSection: FC<CompanionRatesSectionProps> = ({
  companionRates,
  companionRateSchedule,
  planId,
  totalColumns,
  renewalColumns = 0,
  volumeSpan: volumeSpanProp,
  rateSpan: rateSpanProp,
  onRateSaved,
  isProposalRateSchedule,
}) => {
  const axiosInstance = useApi();
  const updateRateMutation = useUpdateRate(axiosInstance);
  const updateProposalRateMutation = useUpdateProposalRate(axiosInstance);
  const rateMutation = isProposalRateSchedule
    ? updateProposalRateMutation
    : updateRateMutation;

  // Volume/Rate span only the current columns; the trailing renewal columns are left blank
  // (the incumbent renewal is a rate-only concept, not a dependent volume/rate).
  const currentColumns = totalColumns - renewalColumns;
  const derivedVolumeSpan = Math.floor((currentColumns - 1) / 2);
  const volumeSpan = volumeSpanProp ?? derivedVolumeSpan;
  const rateSpan = rateSpanProp ?? currentColumns - 1 - derivedVolumeSpan;
  const rateEndBorderRight = renewalColumns > 0 ? AG_BORDER : "none";
  const renewalFillers = Array.from({ length: renewalColumns }, (_, i) => i);

  const handleCompanionLivesCommit = (rate: Rate, value: string): void => {
    if (!companionRateSchedule?.id || !rate?.id) {
      return;
    }
    const numericValue = parseIntBaseTen(value);
    if (isNaN(numericValue) || numericValue === rate.numberOfLives) {
      return;
    }

    rateMutation.mutate(
      {
        scheduleId: companionRateSchedule.id,
        rateId: rate.id,
        benefitPlanId: planId,
        rate: { numberOfLives: numericValue },
      },
      {
        onSuccess: () => onRateSaved(),
      },
    );
  };

  const handleCompanionRateCommit = (rate: Rate, value: string): void => {
    if (!companionRateSchedule?.id || !rate?.id) {
      return;
    }

    const numericValue = parseFloat(value);
    if (isNaN(numericValue) || numericValue === rate.rate) {
      return;
    }

    rateMutation.mutate(
      {
        scheduleId: companionRateSchedule.id,
        rateId: rate.id,
        benefitPlanId: planId,
        rate: { rate: numericValue },
      },
      {
        onSuccess: () => onRateSaved(),
      },
    );
  };

  return (
    <>
      {/* Companion Header Row */}
      <Box sx={gridRowSx}>
        <Box sx={{ ...headerCellSx, borderTop: AG_BORDER }} />
        <Box
          sx={{
            ...headerCellSx,
            ...(volumeSpan > 1 && { gridColumn: `span ${volumeSpan}` }),
            justifyContent: "flex-end",
            borderTop: AG_BORDER,
          }}
        >
          Volume
        </Box>
        <Box
          sx={{
            ...headerCellSx,
            ...(rateSpan > 1 && { gridColumn: `span ${rateSpan}` }),
            justifyContent: "flex-end",
            borderRight: rateEndBorderRight,
            borderTop: AG_BORDER,
          }}
        >
          Rate
        </Box>
        {renewalFillers.map((i) => (
          <Box
            key={`companion-renewal-header-${i}`}
            sx={{
              ...headerCellSx,
              borderLeft: i === 0 ? AG_BORDER : undefined,
              borderRight: i === renewalColumns - 1 ? "none" : undefined,
              borderTop: AG_BORDER,
            }}
          />
        ))}
      </Box>

      {/* Companion Data Rows */}
      {companionRates.map((companionRate, index) => {
        const label = getCompanionRateLabel(companionRate.coverageTierCode);
        // Keep a row separator between companion rows; drop it on the last row.
        // Use AG_BORDER (not undefined) so it doesn't erase the cells' default
        // borderBottom when spread over gridCellSx/headerCellSx.
        const isLastRow = index === companionRates.length - 1;
        const rowBorderBottom = isLastRow ? "none" : AG_BORDER;
        return (
          <Box key={companionRate.id} sx={gridRowSx}>
            <Box sx={{ ...headerCellSx, borderBottom: rowBorderBottom }}>
              {label}
            </Box>
            <RateReviewCell
              planId={planId}
              fieldPath={buildRateFieldPath(
                companionRateSchedule,
                companionRate,
                "numberOfLives",
              )}
              sx={{
                ...gridCellSx,
                ...(volumeSpan > 1 && { gridColumn: `span ${volumeSpan}` }),
                borderBottom: rowBorderBottom,
              }}
            >
              <DebouncedNumericInput
                serverValue={
                  companionRate.numberOfLives != null
                    ? String(companionRate.numberOfLives)
                    : ""
                }
                onCommit={(value) =>
                  handleCompanionLivesCommit(companionRate, value)
                }
                allowDecimals={false}
                sx={inputSx}
                inputProps={{
                  "aria-label": `${label} Volume`,
                  tabIndex: 0,
                }}
              />
            </RateReviewCell>
            <RateReviewCell
              planId={planId}
              fieldPath={buildRateFieldPath(
                companionRateSchedule,
                companionRate,
                "rate",
              )}
              sx={{
                ...gridCellSx,
                ...(rateSpan > 1 && { gridColumn: `span ${rateSpan}` }),
                borderRight: rateEndBorderRight,
                borderBottom: rowBorderBottom,
              }}
            >
              <DebouncedNumericInput
                serverValue={formatRateValue(
                  companionRate.rate,
                  companionRate.unit,
                )}
                onCommit={(value) =>
                  handleCompanionRateCommit(companionRate, value)
                }
                allowDecimals
                isCurrency
                sx={inputSx}
                inputProps={{
                  "aria-label": `${label} Rate`,
                  tabIndex: 0,
                }}
              />
            </RateReviewCell>
            {renewalFillers.map((i) => (
              <Box
                key={`companion-renewal-${companionRate.id}-${i}`}
                sx={{
                  borderLeft: i === 0 ? AG_BORDER : undefined,
                  borderBottom: rowBorderBottom,
                }}
              />
            ))}
          </Box>
        );
      })}
    </>
  );
};
